import React, { useState } from 'react';

/**
 * PasswordField - Reusable password input with built-in show/hide toggle
 *
 * Features:
 * - Eye icon toggle (👁️/🙈)
 * - Helper icon with popover (optional)
 * - Validation states
 * - Auto-mask on blur
 * - Consistent styling
 */
const PasswordField = ({
  label,
  value,
  onChange,
  placeholder = '',
  helpText = '',
  helpIcon = false,
  required = false,
  error = null,
  disabled = false,
  name = ''
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Label with optional help icon */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '6px'
      }}>
        <label
          style={{
            color: 'var(--text-primary)',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--accent-danger)', marginLeft: '4px' }}>*</span>}
        </label>

        {helpIcon && helpText && (
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onMouseEnter={() => setShowHelp(true)}
              onMouseLeave={() => setShowHelp(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'help',
                fontSize: '14px',
                padding: '2px',
                lineHeight: '1'
              }}
            >
              ❓
            </button>

            {/* Help popover */}
            {showHelp && (
              <div
                style={{
                  position: 'absolute',
                  top: '24px',
                  left: '0',
                  zIndex: 1000,
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px',
                  padding: '12px',
                  minWidth: '250px',
                  maxWidth: '350px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  fontSize: '12px',
                  color: 'var(--text-primary)',
                  lineHeight: '1.5'
                }}
              >
                {helpText}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input field with toggle button */}
      <div style={{ position: 'relative' }}>
        <input
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '12px',
            paddingRight: '40px',
            border: error ? '1px solid var(--accent-danger)' : '1px solid var(--border-primary)',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Courier New", monospace',
            background: disabled ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            boxSizing: 'border-box',
            outline: 'none',
            transition: 'border-color 0.2s ease'
          }}
          onFocus={(e) => {
            if (!error) {
              e.target.style.borderColor = 'var(--accent-primary)';
            }
          }}
          onBlur={(e) => {
            if (!error) {
              e.target.style.borderColor = 'var(--border-primary)';
            }
          }}
        />

        {/* Show/hide toggle button */}
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          disabled={disabled}
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            padding: '4px 8px',
            lineHeight: '1',
            opacity: disabled ? 0.5 : 1
          }}
        >
          {showPassword ? '🙈' : '👁️'}
        </button>
      </div>

      {/* Helper text or error message */}
      {!helpIcon && helpText && !error && (
        <small
          style={{
            display: 'block',
            marginTop: '6px',
            color: 'var(--text-secondary)',
            fontSize: '12px'
          }}
        >
          {helpText}
        </small>
      )}

      {error && (
        <small
          style={{
            display: 'block',
            marginTop: '6px',
            color: 'var(--accent-danger)',
            fontSize: '12px'
          }}
        >
          {error}
        </small>
      )}
    </div>
  );
};

export default PasswordField;
